import { addMilliseconds, differenceInMilliseconds, isPast, parseJSON } from "date-fns";
import { first, matches, omit } from "lodash-es";
import find from "lodash-es/find";
import isNil from "lodash-es/isNil";
import isUndefined from "lodash-es/isUndefined";
import { action, autorun, makeAutoObservable, runInAction } from "mobx";
import type { z } from "zod";
import { apiClient, ensureWretchError } from "../../common/apiClient";
import { APIPROXY_HOST } from "../../common/environment";
import { reportError } from "../../common/errorReporting";
import {
   BackendIssueError,
   GenericError,
   ServerNotFoundError,
   WrongCodeError,
   WrongUsernameOrPasswordError
} from "../../common/errors";
import { logger } from "../../common/logger";
import { platformRoutes, resolvePlatformRequestUrl } from "../../common/platformRoutes";
import type { BaseStoreType } from "../../common/types/BaseStoreType";
import type { Company, EnhancedCompany, LoginData, Session, User } from "../../common/types/companyTypes";
import type { ORDERTYPE } from "../../common/types/productOrderTypes";
import {
   type AsyncData,
   initializeWithDefaultData,
   setAsDataAvailable,
   setAsErrorOccured,
   setAsWaitingForData
} from "../../common/utils/asyncDataUtils";
import { formatDateTime } from "../../common/utils/dateUtils";
import {
   getCompanyFromStorage,
   getOrdertypeFromStorage,
   getSessionFromStorage,
   getViewedPromotionArticlesFromStorage,
   setCompanyInStorage,
   setLastCompanyInStorage,
   setSessionInStorage
} from "../../common/utils/storage/storageUtils";
import type {
   AttemptPasswordResetQueryParams,
   ChangePasswordBody,
   GetUserDataResponse,
   InitPasswordResetQueryParams,
   LoginQueryParams,
   LoginResponse,
   RequestOneTimeCodeQueryParams,
   UpdateCompanyDataBody,
   UpdateCompanyDataResponse,
   UpdateCustomerDataBody,
   UpdateCustomerDataResponse,
   VerifyOneTimeCodeQueryParams,
   VerifyOneTimeCodeResponse
} from "../../common/zodSchemas/backend";
import theme from "../../themes/theme";
import cartStore from "../cart/cartStore";
import categoryStore from "../categories/categoryStore";
import contentStore from "../cms/contentStore";
import segmentStore from "../cms/segmentStore";
import deliveryDatesStore from "../deliveryDates/deliveryDatesStore";
import favoriteStore from "../favoriteStore";
import featuresStore from "../features/featuresStore";
import notificationsStore from "../notifications/notificationsStore";
import posthogStore from "../posthogStore";
import productStore from "../product/productStore";
import promotionStore from "../promotionStore";
import subscriptionStore from "../subscriptionStore";
import toastStore from "../toastStore";
import {
   calculateAuthSessionRefreshDelay,
   consumableAuthQueryParams,
   loginAuthSession,
   logoutAuthSession,
   refreshAuthSession,
   resolveAuthSessionFromQuery,
   verifySmsAuthSession
} from "./authSessionFacade";
import { enhanceCompany } from "./authUtils";
import loginState from "./loginState";

/** Configuration settings for scheduled refresh of auth tokens */
let refreshTimeoutId: ReturnType<typeof setTimeout> | undefined;
let logoutTimeoutId: ReturnType<typeof setTimeout> | undefined;

export type LoginRequirement =
   | "CART"
   | "ORDER_CONFIRMATION"
   | "PICKUP_CONFIRMATION"
   | "SEGMENTED_CONTENT"
   | "ACCOUNT_PAGES"
   | "NOT_REQUIRED";

type AuthStoreType = BaseStoreType & {
   user: User | null;
   session: Session | null;
   companies: EnhancedCompany[] | null;
   currentCompany: string | null;
   currentlyRequiresLogin: LoginRequirement[];
   codeChallengeId: string | null;
   smsLoginIdentifier: string | null;
   resetPasswordId: AsyncData<string | null>;
   resetPasswordProgress: AsyncData<boolean>;
   startStep?: number;
   switchingCompany: boolean;
   username?: string;
   error?: BackendIssueError | null;
   //readonly loggedIn: boolean;

   isLoggedIn(): boolean;
   isPunchoutSession(): boolean;
   getSessionToken(): string | null;
   refreshToken(): Promise<void>;
   scheduleTokenRefresh(session: Session): void;
   abortTokenTimers(): void;
   getSessionFromQuery(): Promise<boolean>;
   verifySession(session: Session | null): Promise<unknown>;
   verifyStillLoggedIn(visible: boolean): Promise<void>;
   changeCompany(newCompany: string): Promise<void>;
   getCurrentCompany(): EnhancedCompany | undefined;
   getCustomerData(token: string): Promise<LoginData>;
   updatePersonalContactInfo(email: string, firstName: string, lastName: string, mobile: string): Promise<void>;
   updateCompanyContactInfo(email: string, phone: string, phone2: string): Promise<void>;
   updateCompanyShippingAddress(address: {
      companyName: string;
      streetLine1: string;
      streetLine2: string;
      streetLine3: string;
      postalCity: string;
      postalCode: string;
   }): Promise<void>;
   getAlgoliaUserToken(): string;
   changePassword(newPassword: string): Promise<string>;
   requestSmsLoginOtp(mobileNumber: string): Promise<void>;
   verifySmsLoginOtp(code: string): Promise<void>;
   requestResetPasswordOtp(email: string): Promise<void>;
   attemptResetPassword(resetCode: string, newPassword: string): Promise<void>;
   clearResetPassword(): void;
   login(username: string, password: string): Promise<void>;
   logoutFromBackend(): Promise<void>;
   logout(): void;
};

type UserDataResponse = z.infer<typeof GetUserDataResponse>;

type LoginQueryParamsType = z.infer<typeof LoginQueryParams>;

type LoginResponseType = z.infer<typeof LoginResponse>;

type UpdateCompanyDataBodyType = z.infer<typeof UpdateCompanyDataBody>;
type UpdateCompanyDataResponseType = z.infer<typeof UpdateCompanyDataResponse>;
type UpdateCustomerDataBodyType = z.infer<typeof UpdateCustomerDataBody>;
type UpdateCustomerDataResponseType = z.infer<typeof UpdateCustomerDataResponse>;

type VerifyOneTimeQueryParamsType = z.infer<typeof VerifyOneTimeCodeQueryParams>;
type VerifyOneTimeCodeResponseType = z.infer<typeof VerifyOneTimeCodeResponse>;

type InitPasswordResetQueryParamsType = z.infer<typeof InitPasswordResetQueryParams>;

type ChangePasswordBodyType = z.infer<typeof ChangePasswordBody>;

type RequestOneTimeCodeQueryParamsType = z.infer<typeof RequestOneTimeCodeQueryParams>;

type AttemptPasswordResetQueryParamsType = z.infer<typeof AttemptPasswordResetQueryParams>;

const authStore: AuthStoreType = makeAutoObservable({
   session: getSessionFromStorage(),
   user: null,
   companies: [],
   currentCompany: null,
   currentlyRequiresLogin: [],
   codeChallengeId: null,
   smsLoginIdentifier: null,
   resetPasswordId: initializeWithDefaultData(null),
   resetPasswordProgress: initializeWithDefaultData(false),
   switchingCompany: false,

   isLoggedIn: () => {
      return loginState.is("LOGGED_IN");
   },

   isPunchoutSession: () => {
      return authStore.user?.loginType === "PUNCHOUT";
   },

   getCustomerData: async (token: string) => {
      const customerDataEndpoint = resolvePlatformRequestUrl(platformRoutes.customerSessionCurrentUser, {
         storeId: theme.storeId
      });
      const profileData: UserDataResponse = await apiClient(customerDataEndpoint, token).get().json();
      const user: User = omit(profileData, "companies") as User;

      const companies: Company[] = profileData.companies.map((company) => {
         const { businessChainLevel1, businessChainLevel2, businessChainLevel3, businessChainLevel4, ...rest } = company;

         return {
            ...rest,
            businessLevels: {
               level1: businessChainLevel1 || "",
               level2: businessChainLevel2 || "",
               level3: businessChainLevel3 || "",
               level4: businessChainLevel4 || ""
            },
            minimumOrderValue: company.minimumOrderValue || 0
         };
      });
      const currentCompany: string | undefined = first(companies)?.customerNumber;
      if (isNil(currentCompany)) {
         throw new BackendIssueError("Unable to determine default customer number");
      }

      return { user, companies, currentCompany };
   },

   getSessionToken: () => {
      const token = authStore.session?.accessToken;
      if (isUndefined(token)) {
         return null;
      }
      return token;
   },

   refreshToken: async () => {
      logger.log("auth", "Refreshing access token using existing access token");

      if (isNil(authStore.user) || isNil(authStore.session)) {
         logger.warn("auth", "The session is no longer defined in the store, aborting refresh...");
         return Promise.resolve();
      }

      const expiryTime = parseJSON(authStore.session.expiry);
      if (isPast(expiryTime)) {
         logger.warn("auth", `Token past expiry time (${expiryTime}), logout user out...`);
         toastStore.addError(
            "Logget ut pga inaktivitet",
            "Du har blitt logget ut på grunn av inaktivitet. Logg inn igjen for å fortsette handleturen.",
            { context: "login", text: "token_past_expiry_error" }
         );

         loginState.transitionToIfPossible("NOT_LOGGED_IN");
         return Promise.resolve();
      }

      const refreshEndpoint = resolvePlatformRequestUrl(platformRoutes.customerSessionRefresh, {});
      const updateSession = action((newSession: Session) => {
         authStore.scheduleTokenRefresh(newSession);
         authStore.session = newSession;
      });

      await refreshAuthSession(refreshEndpoint, updateSession).catch((err) => {
         logger.warn("auth", "Unable to refresh access token, will automatically log user out when token expires...", err);
         reportError(err, { context: "Token refresh" });
      });
   },

   scheduleTokenRefresh: (session) => {
      // Schedule a refresh of the access token so we don't time out
      const expiryTime = parseJSON(session.expiry);
      const expiryIn = differenceInMilliseconds(expiryTime, new Date());

      if (logoutTimeoutId) {
         clearTimeout(logoutTimeoutId);
      }
      logoutTimeoutId = setTimeout(() => loginState.transitionToIfPossible("NOT_LOGGED_IN"), expiryIn);

      if (refreshTimeoutId) {
         clearTimeout(refreshTimeoutId);
      }
      const refreshIn = calculateAuthSessionRefreshDelay(expiryIn);
      const refreshAt = addMilliseconds(new Date(), refreshIn);
      logger.log("auth", `Scheduling refresh of access token in ${refreshIn} ms (${formatDateTime(refreshAt)})`);

      refreshTimeoutId = setTimeout(() => void authStore.refreshToken(), refreshIn);
   },

   abortTokenTimers: () => {
      if (refreshTimeoutId) {
         clearTimeout(refreshTimeoutId);
         refreshTimeoutId = undefined;
      }
      if (logoutTimeoutId) {
         clearTimeout(logoutTimeoutId);
         logoutTimeoutId = undefined;
      }
   },

   getSessionFromQuery: async () => {
      const params = new URLSearchParams(window.location.search);
      const consumableParams = consumableAuthQueryParams(params);

      try {
         const session = await resolveAuthSessionFromQuery(params, () =>
            resolvePlatformRequestUrl(platformRoutes.customerSessionBootstrap, {
               storeId: theme.storeId
            })
         );
         if (session === null) {
            return false;
         }

         logger.log("auth", "Creating session data from query string");
         authStore.session = session;
         return true;
      } finally {
         const currentParams = new URLSearchParams(window.location.search);
         if (consumableParams.some((param) => currentParams.has(param))) {
            for (const consumableParam of consumableParams) {
               currentParams.delete(consumableParam);
            }
            const query = currentParams.toString();
            const nextUrl = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
            window.history.replaceState({}, document.title, nextUrl);
         }
      }
   },

   verifySession: async (session = null): Promise<void> => {
      const { username } = authStore;
      const usableSession = session === null && authStore.session !== null ? authStore.session : session;

      if (usableSession === null) {
         loginState.transitionTo("NOT_LOGGED_IN");
         return;
      }

      loginState.transitionTo("VERIFY_SESSION");

      let customerResult: LoginData;
      try {
         customerResult = await authStore.getCustomerData(usableSession.accessToken);
      } catch (err) {
         const wErr = ensureWretchError(err);
         if (!isNil(wErr)) {
            logger.log("auth", "Token is likely expired. Response: ", wErr.status);
         }
         loginState.transitionTo("NOT_LOGGED_IN");
         return;
      }

      try {
         logger.log("auth", "Customer Data verified:", customerResult);
         runInAction(() => {
            loginState.transitionTo("LOGGING_IN");
            authStore.session = usableSession;
            authStore.user = customerResult.user;
            authStore.companies = customerResult.companies.map((company) => enhanceCompany(company));
         });

         // The store cannot be used safely without products. A failed load is retried once before logging out.
         try {
            await productStore.productsReady.catch(() => productStore.loadProducts());
         } catch (err) {
            logger.warn("auth", "Product catalog is unavailable, forcing logout", err);
            loginState.forceLogout("PRODUCT_CATALOG_UNAVAILABLE");
            return;
         }

         const { companies, currentCompany } = authStore;
         const usernameCompany = companies?.find((company) => company.customerNumber === username)?.customerNumber;
         const sessionCompany = getCompanyFromStorage();
         const currentCompanyNumber = currentCompany || usernameCompany || sessionCompany;

         if (currentCompanyNumber) {
            await authStore.changeCompany(currentCompanyNumber);
         } else {
            let company: EnhancedCompany | undefined;
            if (companies?.length === 1) {
               logger.log("auth", "COMPANY SELECTION: Only one company available, selecting it automatically");
               company = companies[0];
            } else {
               logger.log("auth", `COMPANY SELECTION: Multiple companies available, checking if username ${username} matches`);
               company = companies?.find((company) => username === company.customerNumber);
            }

            if (company) {
               logger.log("auth", `COMPANY SELECTION: Company found ${company.customerNumber}, proceeding with changeCompany...`);
               await authStore.changeCompany(company.customerNumber);
            } else {
               logger.log("auth", "COMPANY SELECTION: Multiple companies available, showing selection screen");
               loginState.transitionTo("COMPANY_SELECTION");
            }
         }

         authStore.scheduleTokenRefresh(usableSession);
      } catch (err) {
         logger.warn("auth", "Error verifying session", err);
         authStore.logout();
         throw err;
      }
   },

   verifyStillLoggedIn: async (visible) => {
      if (!featuresStore.loginAvailable) {
         logger.log("auth", "Login is no longer available! Logging out...");
         loginState.forceLogoutIfPossible("SESSION_EXPIRED");
         authStore.logout();
         return;
      }
      if (!authStore.isLoggedIn() || !authStore.session || !visible) {
         return;
      }

      try {
         const usableSession = authStore.session;
         await authStore.getCustomerData(usableSession.accessToken);
         logger.log("auth", "Session is verified as still active!");
      } catch (_err) {
         if (cartStore.isEditing()) {
            cartStore.items = [];
            if (authStore.isLoggedIn()) {
               cartStore.saveCartToServerWithoutDelay();
            }
         }
         logger.log("auth", "Session is not longer valid! Logging out...");
         loginState.forceLogoutIfPossible("SESSION_EXPIRED");
      }
   },

   changeCompany: async (newCompany) => {
      logger.log("auth", `Switching to company ${newCompany}`);
      loginState.transitionTo("COMPANY_SELECTED");

      authStore.error = undefined;
      try {
         if (isNil(authStore.session)) {
            throw new GenericError("MISSING TOKEN");
         }

         authStore.currentCompany = newCompany;
         setCompanyInStorage(newCompany);
         setLastCompanyInStorage(newCompany);

         const company = authStore.getCurrentCompany();
         if (company === undefined) {
            throw new BackendIssueError("Unable to find selected company");
         }

         segmentStore.updateCurrentCustomerProperties();
         loginState.transitionTo("FEATURES_LOADING");

         await featuresStore.fetchEnabledCustomerFeatures(authStore.currentCompany);
         await featuresStore.fetchEnabledConfigCatFeatures(authStore.getCurrentCompany());

         void favoriteStore.loadFavorites();

         loginState.transitionTo("DELIVERY_DATES_LOADING");

         await deliveryDatesStore.fetchDeliveryDates(authStore.currentCompany);

         const availableOrderTypes = deliveryDatesStore.getAvailableOrderTypes();
         const isOrderChange = cartStore.isEditing() && !cartStore.isEditingSubscription();
         const orderType =
            isOrderChange && cartStore.editing?.orderType
               ? (cartStore.editing.orderType as ORDERTYPE)
               : getOrdertypeFromStorage(theme.defaultOrderType);
         if (isOrderChange) {
            cartStore.changeOrderType(orderType);
            await cartStore.restoreGeneratedOrderDeliveryDate();
         } else if (availableOrderTypes === null || availableOrderTypes.length === 0) {
            cartStore.changeOrderType(orderType); // Should be handeled another way as they don't have ordertype
         } else if (availableOrderTypes.includes(orderType)) {
            cartStore.changeOrderType(orderType);
         } else {
            cartStore.changeOrderType(availableOrderTypes[0]);
         }

         const currentDelivery = deliveryDatesStore.getCurrentDelivery();
         if (currentDelivery === null) {
            authStore.error = new BackendIssueError("Missing delivery dates");
            loginState.transitionTo("LOGGED_IN");
            return;
         }

         loginState.transitionTo("ASSORTMENT_LOADING");

         try {
            await productStore.loadAssortment(authStore.currentCompany, currentDelivery.date, cartStore.orderType);
            if (theme.searchConfig) {
               await categoryStore.updateCategorySkus();
            }
         } catch (err) {
            if (err instanceof BackendIssueError) {
               authStore.error = err;
            } else {
               authStore.error = new BackendIssueError("Unable to load assortment, error from backend");
            }

            loginState.transitionTo("LOGGED_IN");
            return;
         }

         loginState.transitionTo("NOTIFICATIONS_LOADING");
         await promotionStore.fetchAvailablePromotions();
         await promotionStore.fetchSignUps();
         void cartStore.loadCartFromServer();
         void subscriptionStore.fetchSubscriptionList();

         if (featuresStore.notificationsEnabled && theme.notifications.enableDeliveryMessages) {
            await notificationsStore.fetchUnreadNotifications();
            await notificationsStore.fetchReadNotifications();
         }
         contentStore.viewedPromotionArticleIds = getViewedPromotionArticlesFromStorage(authStore.currentCompany);
         posthogStore.posthog?.identify(`${authStore.currentCompany}`, company);
         loginState.transitionTo("LOGGED_IN");
      } catch (err) {
         logger.warn("auth", "Error occured during login", err);

         throw err;
      }
   },

   getCurrentCompany: () => {
      return find(authStore.companies, matches({ customerNumber: authStore.currentCompany }));
   },

   getAlgoliaUserToken: () => `company-${authStore.currentCompany}`,

   changePassword: async (newPassword): Promise<string> => {
      const endpoint = resolvePlatformRequestUrl(platformRoutes.customerSessionPassword, { storeId: theme.storeId });
      try {
         const changePasswordBody: ChangePasswordBodyType = { newPassword };
         await apiClient(endpoint, authStore.getSessionToken()).content("application/json").post(changePasswordBody).text();
         return "";
      } catch (error) {
         logger.log("auth", { error, where: "authStore.changePassword" });
         return error && typeof error === "object" && "text" in error ? (error.text as string) : "unknown error cause";
      }
   },

   updateCompanyContactInfo: async (email, phone1, phone2) => {
      if (!authStore.isLoggedIn() || isNil(authStore.user)) {
         toastStore.addError("Det oppsto en feil", "Prøv å logge ut og inn igjen for å oppdatere profilen.", {
            context: "login",
            text: "not_logged_in_error"
         });
         return;
      }
      const currentCompany = authStore.currentCompany;
      if (isNil(currentCompany)) {
         return;
      }
      const updateCompanyBody: UpdateCompanyDataBodyType = {
         email,
         phone1,
         phone2
      };
      const endpoint = resolvePlatformRequestUrl(platformRoutes.customerSessionCompanyContact, {
         storeId: theme.storeId,
         customerNumber: currentCompany
      });
      const res: UpdateCompanyDataResponseType = await apiClient(endpoint, authStore.getSessionToken())
         .post(updateCompanyBody)
         .json();
      runInAction(() => {
         const infoIsUpdated: boolean = res.email === email && res.phone1 === phone1 && res.phone2 === phone2;
         if (!infoIsUpdated) {
            throw "Unable to save new contact info";
         }

         logger.log("auth", "Updated email, phone and phone2 successfully");

         const companyIndex = authStore.companies?.findIndex((company) => company.customerNumber === currentCompany);
         if (!isNil(authStore.companies) && !isNil(companyIndex)) {
            authStore.companies[companyIndex].email = email;
            authStore.companies[companyIndex].phone1 = phone1;
            authStore.companies[companyIndex].phone2 = phone2;
         }
      });
   },

   updateCompanyShippingAddress: async (addressLines) => {
      if (!authStore.isLoggedIn() || isNil(authStore.user)) {
         throw new Error("User is not logged in");
      }
      const currentCompany = authStore.currentCompany;
      if (isNil(currentCompany)) {
         throw new Error("No company selected");
      }
      const endpoint = resolvePlatformRequestUrl(platformRoutes.customerCompanyShippingAddress, {
         storeId: theme.storeId,
         customerNumber: currentCompany
      });
      var response = await apiClient(endpoint, authStore.getSessionToken())
         .put(addressLines)
         .json()
         .catch((err) => {
            logger.log("auth", "Error updating delivery address: ", err);
            throw new BackendIssueError();
         });

      runInAction(() => {
         const currentCompany = authStore.getCurrentCompany();
         if (!isNil(currentCompany)) {
            currentCompany.shippingAddress = response as string[];
         }
      });
   },

   updatePersonalContactInfo: async (email, firstName, lastName, mobile) => {
      if (!authStore.isLoggedIn() || isNil(authStore.user)) {
         toastStore.addError("Det oppsto en feil", "Prøv å logge ut og inn igjen for å oppdatere profilen.", {
            context: "login",
            text: "not_logged_in_error "
         });
         return;
      }

      const currentCompany = authStore.currentCompany;
      if (isNil(currentCompany)) {
         return;
      }
      const updateCustomerBody: UpdateCustomerDataBodyType = {
         id: authStore.user.id,
         email,
         firstName,
         lastName,
         mobile
      };
      const endpoint = resolvePlatformRequestUrl(platformRoutes.customerSessionCurrentUser, { storeId: theme.storeId });
      const res: UpdateCustomerDataResponseType = await apiClient(endpoint, authStore.getSessionToken())
         .post(updateCustomerBody)
         .json();

      logger.log("auth", "Updated firstName, lastName, email and mobile successfully");

      const authStoreUser = authStore.user;
      runInAction(() => {
         authStoreUser.email = res.email;
         authStoreUser.firstName = res.firstName;
         authStoreUser.lastName = res.lastName;
         authStoreUser.mobile = res.mobile;
      });
   },

   requestSmsLoginOtp: async (mobileNumber) => {
      const params: RequestOneTimeCodeQueryParamsType = {
         mobileNumber
      };
      authStore.smsLoginIdentifier = mobileNumber;
      const endpoint = resolvePlatformRequestUrl(platformRoutes.customerSessionSmsRequestCode, { storeId: theme.storeId });
      authStore.codeChallengeId = await apiClient(endpoint).query(params).post().text();
   },

   verifySmsLoginOtp: async (code) => {
      loginState.transitionTo("CHECKING_LOGIN");

      try {
         const verifyParams: VerifyOneTimeQueryParamsType = {
            challengeId: authStore.codeChallengeId || "",
            code
         };
         const verifyEndpoint = resolvePlatformRequestUrl(platformRoutes.customerSessionSmsVerify, { storeId: theme.storeId });
         const session: VerifyOneTimeCodeResponseType = await verifySmsAuthSession({
            verifyEndpoint,
            params: verifyParams
         });

         logger.log("auth", "Verifying OneTimeCode session", session);
         await authStore.verifySession(session);

         // Clear the codeChallengeId once the progress dialog has disappeared
         setTimeout(() => {
            authStore.codeChallengeId = null;
            authStore.smsLoginIdentifier = null;
         }, 500);
      } catch (err) {
         loginState.transitionTo("LOGIN_MODAL");

         const wErr = ensureWretchError(err);
         if (wErr?.response?.status === 403) {
            throw new WrongCodeError();
         }
         logger.log("auth", "Got error during OneTimeCode login: ", err);
         throw new BackendIssueError("Unable to verify OneTimeCode");
      }
   },

   requestResetPasswordOtp: async (email: string) => {
      const transitionedSuccessfully = setAsWaitingForData(authStore.resetPasswordId);

      if (!transitionedSuccessfully) {
         return Promise.reject();
      }

      const params: InitPasswordResetQueryParamsType = {
         email
      };
      const endpoint = resolvePlatformRequestUrl(platformRoutes.customerSessionForgottenPassword, { storeId: theme.storeId });
      return apiClient(endpoint)
         .query(params)
         .post()
         .text((resetId) => {
            setAsDataAvailable(authStore.resetPasswordId, resetId);
         })
         .catch((errorObject) => {
            setAsErrorOccured(authStore.resetPasswordId, `${errorObject}`);
         });
   },

   attemptResetPassword: async (code: string, newPassword: string) => {
      const transitionedSuccessfully = setAsWaitingForData(authStore.resetPasswordProgress);

      if (!transitionedSuccessfully) {
         return Promise.reject();
      }

      const params: AttemptPasswordResetQueryParamsType = {
         resetId: authStore.resetPasswordId.data || "",
         code,
         newPassword
      };
      const endpoint = resolvePlatformRequestUrl(platformRoutes.customerSessionResetPassword, { storeId: theme.storeId });
      return apiClient(endpoint)
         .query(params)
         .post()
         .res((res) => {
            setAsDataAvailable(authStore.resetPasswordProgress, res.ok);
         })
         .catch((errorObject) => {
            setAsErrorOccured(authStore.resetPasswordProgress, `${errorObject}`);
         });
   },

   clearResetPassword: () => {
      authStore.resetPasswordId = initializeWithDefaultData(null);
      authStore.resetPasswordProgress = initializeWithDefaultData(false);
   },

   login: async (username, password) => {
      loginState.transitionTo("CHECKING_LOGIN");
      logger.log("auth", `Starting login process using host ${APIPROXY_HOST}`);
      // Need to set it to a initial value, because typescript will not understand that the catch-block guarantees a
      // value being set before next try/catch block
      let session: LoginResponseType = { expiry: "", accessToken: "" };

      try {
         // Attempting to get session with access token for user
         const params: LoginQueryParamsType = { username, password };
         const loginEndpoint = resolvePlatformRequestUrl(platformRoutes.customerSessionLogin, { storeId: theme.storeId });
         session = await loginAuthSession({ loginEndpoint, params });
      } catch (err: unknown) {
         loginState.transitionTo("LOGIN_MODAL");
         const wErr = ensureWretchError(err);
         if (wErr !== null) {
            switch (wErr.response.status) {
               case 400:
               case 401:
                  if (wErr.message === "StoreId not found") {
                     throw new BackendIssueError("StoreId not found");
                  }
                  throw new WrongUsernameOrPasswordError();

               case 500:
                  throw new BackendIssueError();
            }
         }
         throw new ServerNotFoundError();
      }

      try {
         logger.log("auth", "Verifying session", session);
         runInAction(() => {
            authStore.username = username;
         });

         await authStore.verifySession(session);
      } catch (err: unknown) {
         logger.log("auth", "Got error during login: ", err);
         loginState.transitionTo("NOT_LOGGED_IN");

         if (err instanceof Error) {
            reportError(err, { context: "Session Verification" });
         }
         if (err instanceof BackendIssueError) {
            throw err;
         }
         throw new BackendIssueError();
      }
   },

   logoutFromBackend: async () => {
      const session = authStore.session;
      if (isNil(session)) {
         logger.log("auth", "No session token available for logging out");
         return;
      }

      try {
         const endpoint = resolvePlatformRequestUrl(platformRoutes.customerSessionLogout, { storeId: theme.storeId });
         await logoutAuthSession(endpoint, session);
         logger.log("auth", "Successfully logged out through backend");
      } catch (err) {
         logger.warn("auth", "Failed to logout through backend, will only logout from frontend", err);
      }
   },

   logout: action(() => {
      void authStore.logoutFromBackend();
      authStore.abortTokenTimers();
      authStore.user = null;
      authStore.session = null;
      authStore.companies = [];
      authStore.currentCompany = null;
      setCompanyInStorage(null);
      posthogStore.posthog?.reset();
   }),

   clearCompanySpecificData: () => {}
} satisfies AuthStoreType);

// Save user info to session storage whenever it changes
autorun(() => setSessionInStorage(authStore.session));

// Make customer info available for Google Analytics and other software outside React
autorun(() => {
   window.isUserLoggedIn = authStore.isLoggedIn();
   window.companyInfo = authStore.getCurrentCompany();
   window.customerInfo = authStore.user;
});

export default authStore;
